import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  timeout: 3600000, // 1 hour global timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're NOT already on the login page
      if (window.location.pathname !== '/login') {
        Cookies.remove('auth_token');
        window.location.href = '/login';
      } else {
        // If we're on login page, just clear the token but don't redirect
        Cookies.remove('auth_token');
      }
      return Promise.reject(error);
    }

    // Show error toast for other errors (but not for login attempts)
    if (!error.config?.url?.includes('/auth/login')) {
      const message = error.response?.data?.message || 'An error occurred';
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  logout: () => {
    Cookies.remove('auth_token');
    window.location.href = '/login';
  }
};

// Batches API
export const batchAPI = {
  list: (params) => api.get('/batches', { params }),
  get: (id) => api.get(`/batches/${id}`),
  create: (data) => api.post('/batches', data),
  update: (id, data) => api.put(`/batches/${id}`, data),
  delete: (id) => api.delete(`/batches/${id}`),
  getLanguages: () => api.get('/batches/languages'),
  upload: (id, files, onUploadProgress) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    
    return api.post(`/batches/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 3600000, // 1 hour for large uploads
      onUploadProgress
    });
  },
  process: (id) => api.post(`/batches/${id}/process`),
  getStatus: (id) => api.get(`/batches/${id}/status`),
  getImages: (id, params) => api.get(`/batches/${id}/images`, { params }),

  // Sorting: quick-sort from an uploaded RTF, and save mode/custom order
  importSortRtf: (id, file) => {
    const formData = new FormData();
    formData.append('rtf', file);
    return api.post(`/batches/${id}/sort/import-rtf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
  },
  updateSort: (id, payload) => api.put(`/batches/${id}/sort`, payload),
  
  // UPDATED: Enhanced download method with format support
  downloadExport: async (batchId, format = 'excel') => {
    try {
      // Set appropriate headers based on format
      let acceptHeader;
      let defaultFilename;
      
      switch (format.toLowerCase()) {
        case 'excel':
        case 'xlsx':
          acceptHeader = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          defaultFilename = `batch_${batchId}_results.xlsx`;
          break;
        case 'pdf':
          acceptHeader = 'application/pdf';
          defaultFilename = `batch_${batchId}_results.pdf`;
          break;
        case 'word':
        case 'docx':
          acceptHeader = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          defaultFilename = `batch_${batchId}_results.docx`;
          break;
        case 'csv':
          acceptHeader = 'text/csv';
          defaultFilename = `batch_${batchId}_results.csv`;
          break;
        default:
          acceptHeader = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          defaultFilename = `batch_${batchId}_results.xlsx`;
      }

      const response = await api.get(`/batches/${batchId}/download`, {
        params: { format },
        responseType: 'blob',
        timeout: 900000, // 15 minutes for download
        headers: {
          'Accept': acceptHeader
        }
      });
      
      // Extract filename from response headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = defaultFilename;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      return {
        blob: response.data,
        filename,
        format,
        response
      };
    } catch (error) {
      console.error(`Download ${format} error:`, error);
      throw error;
    }
  },
  
  // Backward compatibility methods
  download: (id) => api.get(`/batches/${id}/download`, { 
    responseType: 'blob',
    timeout: 900000, // 15 minutes
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
  }),
  downloadExcel: (batchId) => batchAPI.downloadExport(batchId, 'excel'),
  downloadPDF: (batchId) => batchAPI.downloadExport(batchId, 'pdf'),
  downloadWord: (batchId) => batchAPI.downloadExport(batchId, 'word'),
  downloadCSV: (batchId) => batchAPI.downloadExport(batchId, 'csv')
};

// Images API
export const imageAPI = {
  get: (id) => api.get(`/images/${id}`),
  updateAltText: (id, data) => api.put(`/images/${id}/alt-text`, data),
  setReviewStatus: (id, reviewed) => api.put(`/images/${id}/review-status`, { reviewed }),
  reprocess: (id, data) => api.post(`/images/${id}/reprocess`, data),
  getHistory: (id, params) => api.get(`/images/${id}/history`, { params }),
  downloadHistory: (id) => api.get(`/images/${id}/history/download`, { 
    responseType: 'blob' 
  })
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  createUser: (data) => api.post('/auth/register', data),
  resetPassword: (userId, data) => api.put(`/auth/users/${userId}/reset-password`, data),
  changeUserPassword: (userId, data) => api.put(`/auth/users/${userId}/change-password`, data),
  
  // Language management
  getLanguages: () => api.get('/admin/languages'),
  createLanguage: (data) => api.post('/admin/languages', data),
  updateLanguage: (id, data) => api.put(`/admin/languages/${id}`, data),
  deleteLanguage: (id) => api.delete(`/admin/languages/${id}`)
};

// User Management API
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  changePassword: (id, data) => api.put(`/users/${id}/password`, data),
  toggleStatus: (id, data) => api.put(`/users/${id}/status`, data),
  delete: (id) => api.delete(`/users/${id}`)
};

// ENHANCED: Utility functions with better download handling
export const downloadFile = (blob, filename) => {
  try {
    if (!blob || blob.size === 0) {
      console.error('Invalid blob provided for download');
      return false;
    }
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    return false;
  }
};

// NEW: Enhanced download function specifically for Excel files
export const downloadExcelFile = (blob, filename, fallbackName = 'download.xlsx') => {
  try {
    // Ensure the blob is the correct type
    const excelBlob = new Blob([blob], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    // Use the provided filename or fallback
    const finalFilename = filename || fallbackName;
    
    downloadFile(excelBlob, finalFilename);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    return false;
  }
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(amount);
};

export default api;